import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';

export interface Location {
  id?: number; // ID temporal para debugging
  code: string;
  quantity: number;
}

export interface ProductInfo {
  id: string;
  name: string;
  image: string;
  code: string;
}

export interface LocationsModalProps {
  product?: ProductInfo;
  locations?: Location[];
  namespace?: string;
  dataTestId?: string;
}

export const LocationsModal = forwardRef<ModalCenterRef, LocationsModalProps>(
  ({ product, locations = [], namespace = 'packingModule', dataTestId = 'locations-modal' }, ref) => {
    const { t } = useTranslation(namespace);

    return (
      <ModalCenter ref={ref} title={t('modals.locations.title')} type="default" width="lg">
        {product && (
          <div className="space-y-4">
            {/* Información del producto */}
            <div className="pb-4 border-b border-lines">
              <Product
                id={parseInt(product.id)}
                name={product.name}
                src={product.image}
                code={product.code}
                showTooltip={true}
                enableLineClamp={false}
                data-testid={`${dataTestId}-product-${product.id}`}
              />
            </div>

            {/* Locations Table - Tabla de ubicaciones */}
            <div className="space-y-2">
              <div className="grid grid-cols-5 gap-4 text-sm font-medium text-title border-b border-card-border pb-2">
                <span>ID</span>
                <span className="col-span-2">{t('modals.locations.location_code')}</span>
                <span>{t('modals.locations.quantity')}</span>
              </div>

              {locations.length > 0 ? (
                <div className="space-y-2">
                  {locations.map((location, index) => (
                    <div
                      key={location.code}
                      className={`grid grid-cols-5 gap-4 items-center py-2 ${
                        index < locations.length - 1 ? 'border-b border-card-border' : ''
                      }`}
                      data-testid={`${dataTestId}-location-${location.code}`}
                    >
                      <span className="text-sm text-title">{location.id || 'N/A'}</span>
                      <span className="text-sm text-title col-span-2">{location.code}</span>
                      <span className="text-sm font-medium text-title">{location.quantity}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-subtext">{t('modals.locations.no_locations')}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </ModalCenter>
    );
  },
);

LocationsModal.displayName = 'LocationsModal';
